import { Form, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import {
  getAllowances,
  getIncomeExpense,
  patchIncomeExpense,
} from "@/features/worksheets/api";
import { AutosaveForm } from "@/features/worksheets/components/autosave-form";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents, formatMoney } from "@/features/worksheets/money";
import type { Allowances, IncomeExpense, VariableRow } from "@/features/worksheets/types";

// --- income fields -----------------------------------------------------------

const INCOME_FIELDS: { name: string; label: string }[] = [
  { name: "ssPrimary", label: "Social Security (Taxpayer)" },
  { name: "ssSpouse", label: "Social Security (Spouse)" },
  { name: "pensionPrimary", label: "Pension (Taxpayer)" },
  { name: "pensionSpouse", label: "Pension (Spouse)" },
  { name: "additionalInc", label: "Additional Income" },
  { name: "interest", label: "Interest" },
  { name: "dividends", label: "Dividends" },
  { name: "rentalInc", label: "Rental Income" },
  { name: "distributions", label: "Distributions" },
  { name: "supportInc", label: "Support Income" },
  { name: "alimonyInc", label: "Alimony Income" },
  { name: "unemploymentInc", label: "Unemployment (Taxpayer)" },
  { name: "unempSpouseInc", label: "Unemployment (Spouse)" },
  { name: "other1Inc", label: "Other Income" },
];

// --- expense fields ----------------------------------------------------------
// `allowed` maps to a key on the Allowances object for the read-only IRS column.

type AllowedKey = keyof Allowances;

interface ExpenseSpec {
  label: string;
  actual: string;
  proposed?: string;
  allowed?: AllowedKey;
}

interface ExpenseGroup {
  title: string;
  note?: string;
  rows: ExpenseSpec[];
}

const EXPENSE_GROUPS: ExpenseGroup[] = [
  {
    title: "Food & Clothing",
    rows: [
      { label: "Food", actual: "foodExp", proposed: "foodProp", allowed: "food" },
      { label: "Housekeeping Supplies", actual: "housekeepExp", proposed: "housekeepProp", allowed: "housekeeping" },
      { label: "Apparel & Services", actual: "clothesExp", proposed: "clothesProp", allowed: "apparel" },
      { label: "Personal Care", actual: "personalExp", proposed: "personalProp", allowed: "personalCare" },
      { label: "Miscellaneous", actual: "miscExp", proposed: "miscProp", allowed: "misc" },
    ],
  },
  {
    title: "Housing & Utilities",
    note: "Mortgage / rent payments link to the property records on the Assets tab.",
    rows: [
      { label: "Rent / Mortgage", actual: "rentExp", proposed: "housingProp", allowed: "housing" },
      { label: "Utilities (Gas/Electric/Water)", actual: "dwpExp" },
      { label: "Telephone", actual: "phoneExp" },
      { label: "Real Estate Tax", actual: "reTaxExp" },
      { label: "Repairs & Maintenance", actual: "reRepairExp" },
    ],
  },
  {
    title: "Transportation",
    rows: [
      { label: "Vehicle 1 Ownership", actual: "vehicleExp", proposed: "vehicleProp", allowed: "vehicleOwnership" },
      { label: "Vehicle 2 Ownership", actual: "vehicle2Exp", proposed: "vehicle2Prop" },
      { label: "Operating Costs", actual: "operatingExp", proposed: "operatingProp", allowed: "vehicleOperating" },
      { label: "Public Transportation", actual: "pubtransExp", proposed: "pubtransProp", allowed: "publicTransit" },
    ],
  },
  {
    title: "Retirement",
    rows: [
      { label: "Required Contributions", actual: "retReqExp" },
      { label: "Voluntary Contributions", actual: "retVolExp", proposed: "retProp" },
    ],
  },
  {
    title: "Court-ordered",
    rows: [
      { label: "Court Payments", actual: "courtExp", proposed: "courtProp" },
      { label: "Child Support", actual: "courtChildExp" },
      { label: "Alimony", actual: "courtAlimExp" },
    ],
  },
  {
    title: "Health",
    rows: [
      { label: "Health Insurance", actual: "healthInsExp", proposed: "healthInsProp" },
      { label: "Out-of-Pocket Health Care", actual: "healthExp", proposed: "healthProp", allowed: "healthOop" },
    ],
  },
  {
    title: "Miscellaneous",
    rows: [
      { label: "Child / Dependent Care", actual: "childExp", proposed: "childProp" },
      { label: "Life Insurance", actual: "lifeInsExp", proposed: "lifeInsProp" },
      { label: "Union Dues", actual: "unionExp", proposed: "unionProp" },
      { label: "Student Loans", actual: "studentExp", proposed: "studentProp" },
      { label: "Estimated Tax", actual: "estTaxExp", proposed: "estTaxProp" },
      { label: "Taxes", actual: "taxExp", proposed: "taxProp" },
      { label: "Securing / Storage", actual: "secureExp", proposed: "secureProp" },
      { label: "Delinquent State/Local Tax", actual: "delinqTaxExp", proposed: "delinqTaxProp" },
      { label: "Other", actual: "otherExp", proposed: "otherProp" },
      { label: "Other Withholding", actual: "otherWithExp", proposed: "otherWithProp" },
      { label: "Legal", actual: "legalExp", proposed: "legalProp" },
      { label: "Tax Preparation", actual: "taxPrepExp", proposed: "taxPrepProp" },
      { label: "Tax Resolution", actual: "taxResExp", proposed: "taxResProp" },
      { label: "Charitable Contributions", actual: "charityExp", proposed: "charityProp" },
      { label: "Education", actual: "educationExp", proposed: "educationProp" },
      { label: "Involuntary Deductions", actual: "involuntaryExp", proposed: "involuntaryProp" },
      { label: "Credit Card Payments", actual: "creditCardExp", proposed: "creditCardProp" },
    ],
  },
];

/** Extra proposed-only money fields on the incexp row not surfaced as a paired row. */
const EXTRA_MONEY_FIELDS = ["wagesTaxProp", "ficaTaxProp", "operatingOic"] as const;

/** Every money field name that Formik holds as a dollar string. */
const MONEY_FIELDS: string[] = [
  ...INCOME_FIELDS.map((f) => f.name),
  ...EXPENSE_GROUPS.flatMap((g) =>
    g.rows.flatMap((r) => (r.proposed ? [r.actual, r.proposed] : [r.actual])),
  ),
  ...EXTRA_MONEY_FIELDS,
];

// --- helpers -----------------------------------------------------------------

/** Sum a set of dollar-string Formik fields → cents. */
function sumCents(values: Record<string, unknown>, names: string[]): number {
  return names.reduce((acc, n) => acc + dollarsToCents(values[n] as string), 0);
}

/** Build Formik initial values from the incexp record (cents → dollar strings). */
function toInitialValues(ie: IncomeExpense): Record<string, string> {
  const out: Record<string, string> = {};
  for (const name of MONEY_FIELDS) {
    const v = ie[name];
    out[name] = typeof v === "number" ? centsToDollars(v) : "";
  }
  return out;
}

/**
 * One expense line: Actual + Proposed MoneyFields with a read-only IRS-Allowed
 * display when the category maps to an allowance.
 */
function ExpenseRow({
  label,
  actualName,
  proposedName,
  allowed,
}: {
  label: string;
  actualName: string;
  proposedName?: string;
  allowed?: number;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
      <p className="text-sm font-medium sm:col-span-2">{label}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <MoneyField name={actualName} label="Actual" />
        {proposedName ? (
          <MoneyField name={proposedName} label="Proposed" />
        ) : (
          <div className="hidden sm:block" aria-hidden />
        )}
        <div className="grid gap-2">
          <span className={FIELD_LABEL}>IRS Allowed</span>
          <p className="text-muted-foreground py-2 text-sm tabular-nums">
            {allowed != null ? formatMoney(allowed) : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Live running total of a list of dollar-string fields, formatted as money. */
function ColumnTotal({ label, names }: { label: string; names: string[] }) {
  const { values } = useFormikContext<Record<string, unknown>>();
  const cents = sumCents(values, names);
  return (
    <div className="flex items-center justify-between px-6 py-3 text-sm font-semibold">
      <span>{label}</span>
      <span className="tabular-nums">{formatMoney(cents)}</span>
    </div>
  );
}

// --- variable rows -----------------------------------------------------------

/**
 * Editable list of {desc, amount} rows persisted by patching the whole array
 * (cents) back to the incexp record. Local state; commits on blur/change via a
 * debounced patch on every edit.
 */
function VariableRows({
  title,
  initial,
  fieldName,
  caseId,
}: {
  title: string;
  initial: VariableRow[];
  fieldName: "otherIncome" | "otherAllowed";
  caseId: string;
}) {
  const [rows, setRows] = useState<{ desc: string; amount: string }[]>(
    initial.map((r) => ({ desc: r.desc, amount: centsToDollars(r.amount) })),
  );

  function persist(next: { desc: string; amount: string }[]) {
    const payload = next
      .filter((r) => r.desc.trim() !== "" || r.amount.trim() !== "")
      .map((r) => ({ desc: r.desc, amount: dollarsToCents(r.amount) }));
    void patchIncomeExpense(caseId, { [fieldName]: payload });
  }

  function update(idx: number, patch: Partial<{ desc: string; amount: string }>) {
    setRows((prev) => {
      const next = prev.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      return next;
    });
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{title}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setRows((prev) => [...prev, { desc: "", amount: "" }])}
        >
          <FiPlus className="size-4" /> Add Row
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="text-muted-foreground text-sm">Nothing added yet.</p>
      )}
      {rows.map((row, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_auto_auto] items-end gap-3">
          <div className="grid gap-2">
            <span className={FIELD_LABEL}>Description</span>
            <Input
              value={row.desc}
              onChange={(e) => update(idx, { desc: e.target.value })}
              onBlur={() => persist(rows)}
            />
          </div>
          <div className="grid gap-2">
            <span className={FIELD_LABEL}>Amount</span>
            <Input
              inputMode="decimal"
              placeholder="0.00"
              className="w-32"
              value={row.amount}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) update(idx, { amount: raw });
              }}
              onBlur={() => persist(rows)}
            />
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Remove row"
            onClick={() => {
              const next = rows.filter((_, i) => i !== idx);
              setRows(next);
              persist(next);
            }}
          >
            <FiTrash2 className="text-destructive size-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

// --- tab ---------------------------------------------------------------------

export function IncexpTab({ caseId }: { caseId: string }) {
  const [incexp, setIncexp] = useState<IncomeExpense | null>(null);
  const [allowances, setAllowances] = useState<Allowances | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([getIncomeExpense(caseId), getAllowances(caseId)])
      .then(([ie, al]) => {
        if (!active) return;
        setIncexp(ie);
        setAllowances(al);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [caseId]);

  if (loading || !incexp || !allowances) {
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }

  const incomeFieldNames = INCOME_FIELDS.map((f) => f.name);

  return (
    <Formik initialValues={toInitialValues(incexp)} onSubmit={() => {}}>
      <Form className="grid gap-6 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <AutosaveForm
            save={(patch) => patchIncomeExpense(caseId, patch)}
            moneyFields={MONEY_FIELDS}
          />
        </div>

        {/* Income column */}
        <Card className="gap-4 self-start">
          <div className="px-6">
            <h3 className="font-semibold">Monthly Income</h3>
            <p className="text-muted-foreground text-sm">
              Wages computed from employers — not collected here.
            </p>
          </div>
          <div className="grid gap-4 px-6 sm:grid-cols-2">
            {INCOME_FIELDS.map((f) => (
              <MoneyField key={f.name} name={f.name} label={f.label} />
            ))}
          </div>
          <Separator />
          <div className="px-6">
            <VariableRows
              title="Other Income"
              initial={incexp.otherIncome}
              fieldName="otherIncome"
              caseId={caseId}
            />
          </div>
          <Separator />
          <ColumnTotal label="Total (excl. wages & other income)" names={incomeFieldNames} />
        </Card>

        {/* Expense column */}
        <div className="grid gap-6 self-start">
          {EXPENSE_GROUPS.map((group) => (
            <Card key={group.title} className="gap-4">
              <div className="px-6">
                <h3 className="font-semibold">{group.title}</h3>
                {group.note && (
                  <p className="text-muted-foreground text-sm">{group.note}</p>
                )}
              </div>
              <div className="grid gap-5 px-6">
                {group.rows.map((r) => (
                  <ExpenseRow
                    key={r.actual}
                    label={r.label}
                    actualName={r.actual}
                    proposedName={r.proposed}
                    allowed={r.allowed ? allowances[r.allowed] : undefined}
                  />
                ))}
              </div>
            </Card>
          ))}

          <Card className="gap-4">
            <div className="px-6">
              <h3 className="font-semibold">Other Allowed Expenses</h3>
            </div>
            <div className="px-6">
              <VariableRows
                title="Other Allowed"
                initial={incexp.otherAllowed}
                fieldName="otherAllowed"
                caseId={caseId}
              />
            </div>
          </Card>

          <ExpenseTotals />
        </div>
      </Form>
    </Formik>
  );
}


/** Running totals of actual + proposed expense columns. */
function ExpenseTotals() {
  const actualNames = EXPENSE_GROUPS.flatMap((g) => g.rows.map((r) => r.actual));
  const proposedNames = EXPENSE_GROUPS.flatMap((g) =>
    g.rows.flatMap((r) => (r.proposed ? [r.proposed] : [])),
  );
  return (
    <Card className="gap-0 p-0">
      <ColumnTotal label="Total Actual Expenses" names={actualNames} />
      <Separator />
      <ColumnTotal label="Total Proposed Expenses" names={proposedNames} />
    </Card>
  );
}
